import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Zap, Loader2 } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Lien d'invitation manquant."); return; }
    api.get(`/auth/invite/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(apiErr(e.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, name, password });
      setUser(data);
      toast.success("Compte créé, bienvenue !");
      navigate("/");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8 grid-noise">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="mb-8 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground"><Zap size={20} strokeWidth={2.5} /></div>
          <span className="font-display text-2xl font-bold">GestionPro</span>
        </div>

        {error ? (
          <>
            <h1 className="font-display text-2xl font-bold">Invitation invalide</h1>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="invite-error">{error}</p>
            <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:underline">Retour à la connexion</Link>
          </>
        ) : !info ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold">Rejoindre {info.company_name}</h1>
            <p className="mt-2 text-sm text-muted-foreground">Créez votre compte pour <strong>{info.email}</strong>.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Votre nom</label>
                <input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-primary" placeholder="Jean Dupont" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Mot de passe</label>
                <input data-testid="invite-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-primary" placeholder="••••••••" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Confirmer le mot de passe</label>
                <input data-testid="invite-confirm-password" type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-primary" placeholder="••••••••" />
                {confirmPassword && password !== confirmPassword && (
                  <p className="mt-1.5 text-xs text-red-500" data-testid="invite-password-mismatch">Les mots de passe ne correspondent pas</p>
                )}
              </div>
              <button type="submit" disabled={loading} data-testid="invite-submit" className="flex w-full items-center justify-center gap-2 bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60">
                {loading && <Loader2 size={16} className="animate-spin" />} Créer mon compte
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
